/**
 * Client-side JWT utilities — decode only, no signature verification.
 * Used exclusively for expiry checks and token-type detection.
 */
export class JwtUtils {
    /**
     * Decode the JWT payload without verifying the signature.
     * Returns the parsed payload object, or null if the token is malformed.
     */
    static decode(token) {
        if (!token || typeof token !== 'string') return null;
        try {
            const parts = token.split('.');
            if (parts.length < 2) return null;
            const pad = (s) => s + '==='.slice((s.length + 3) % 4);
            const json = atob(pad(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
            return JSON.parse(json);
        } catch {
            return null;
        }
    }

    /**
     * Returns the expiry timestamp in milliseconds from the JWT `exp` claim,
     * adjusted 30 seconds early for clock skew. Returns null when exp is absent.
     */
    static expiresAt(token) {
        const payload = JwtUtils.decode(token);
        if (!payload?.exp) return null;
        return (payload.exp - 30) * 1000;
    }

    /**
     * Returns true when the token's `exp` claim is within `skewSec` seconds of now,
     * or when exp is missing (treat unknown expiry as expired).
     */
    static isExpired(token, skewSec = 60) {
        const payload = JwtUtils.decode(token);
        if (!payload?.exp) return true;
        return Date.now() >= (payload.exp - skewSec) * 1000;
    }

    /** Returns true when the token carries the `mimir_local_refresh` claim. */
    static isLocalRefreshToken(token) {
        return Boolean(JwtUtils.decode(token)?.mimir_local_refresh);
    }
}
